import {
  PROPERTY_GRID_SUBJECT_PROPERTY,
  SelectedSource,
  expected,
  isDefined,
  isUndefined,
  schSourceType,
} from '@aloft/toolkit-shared';
import { Block, Flex, Icon, Input, Tooltip, Typography, theme } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import {
  Button,
  ButtonProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@aloft/toolkit-sui/chakra';

import React, { useEffect, useRef, useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { resolveFeatureGridFieldConflicts } from '../../../redux/features/actions/resolveFeatureGridFieldConflicts.ts';
import { selectFeatureGridConflictedFields } from '../../../redux/features/selectors/uiSelectors.ts';
import { addFeatureGridConflictedField } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridFieldName, PropertyGridForm } from '../types.ts';
import {
  getDataSourceDisplayInfo,
  getSelectSourceTooltip,
  handleUpdatePropertyData,
} from '../utils.ts';
import { editableFieldOutlineSx } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';

type SourceType = z.infer<typeof schSourceType>;

export const EditableField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
  setValue,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const conflictedFields = useAppSelector(selectFeatureGridConflictedFields);
  const columns = getValues('columns');

  const { fieldName: rawFieldName, toDisplayValue, toEditValue, toValue } = field;

  const [currentParsedValue, setCurrentParsedValue] = useState<string | undefined>(undefined);
  const [currentSource, setCurrentSource] = useState<SourceType | undefined>(undefined);

  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const arvFieldName = (rawFieldName as string).replace(
    /^editableData\./,
    'editableData.arv.',
  ) as PropertyGridFieldName;
  const fieldName = isSubject && isArv ? arvFieldName : rawFieldName;

  const propertyDataFieldName: FieldPath<PropertyGridForm> =
    `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>;
  const selectedSourceFieldName =
    `columns.${columnIndex}.propertyData.selectedSources.${fieldName}` as FieldPath<PropertyGridForm>;
  const selectedSourceFieldValue = useWatch({ control, name: selectedSourceFieldName }) as
    | SelectedSource
    | undefined;
  const selectedSourceValue = selectedSourceFieldValue?.source;
  const value = useWatch({ control, name: propertyDataFieldName }) as string | undefined;

  const { conflictIsResolved } = selectedSourceFieldValue ?? {};

  const [showConflictButton, setShowConflictButton] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const customInput = useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement | null>;

  const propertyDataSourceColumns = columns.filter(
    (column) => column?.propertyId === propertyId && column?.type !== 'property',
  );

  const options = propertyDataSourceColumns
    .map(({ type: source }) => {
      const dataSourceColumnIndex = columns.findIndex(
        (column) => column?.propertyId === propertyId && column?.type === source,
      );

      if (dataSourceColumnIndex === -1) return null;

      const optionValue = getValues(
        `columns.${dataSourceColumnIndex}.propertyData.${rawFieldName}` as FieldPath<PropertyGridForm>,
      ) as string | undefined;

      return {
        value: toDisplayValue ? toDisplayValue(optionValue, propertyId) : optionValue,
        source: source as SourceType,
      };
    })
    .filter(
      (option): option is { value: string | undefined; source: SourceType } =>
        option !== null && !isUndefined(option.value),
    );

  const updatePropertyData = () => {
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string,
    newSource?: SourceType,
  ) => {
    const parsedNewValue = toValue ? toValue(newValue, propertyId) : newValue;
    setCurrentParsedValue(parsedNewValue);
    onChange(parsedNewValue);

    // We need to re-call toDisplayValue here to ensure we get the correct display value.
    // This is necessary for some field types like AloftArea where the field accepts a string output
    // but the toDisplayValue() function's result is a formatted area string.
    const displayParsedValue = toDisplayValue
      ? toDisplayValue(parsedNewValue, propertyId)
      : parsedNewValue;
    const matchingOption = options.find((option) => option.value === displayParsedValue);

    let resolvedSource: SourceType = 'none';
    if (newSource) {
      resolvedSource = newSource;
    } else if (matchingOption) {
      resolvedSource = matchingOption.source;
    } else if (isDefined(parsedNewValue)) {
      resolvedSource = 'custom';
    }

    setValue(`${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>, resolvedSource);
    setCurrentSource(resolvedSource);
  };

  // A field has a conflict when not all the options have the same value and the conflict was not already resolved.
  const fieldHasConflict = Object.keys(conflictedFields).includes(propertyDataFieldName);

  useEffect(() => {
    if (fieldHasConflict && conflictIsResolved) {
      dispatch(
        resolveFeatureGridFieldConflicts({
          [propertyDataFieldName]: expected(conflictedFields[propertyDataFieldName]),
        }),
      );
    }

    if (options.length === 0 || conflictIsResolved || fieldHasConflict) return;

    const allOptionsMatch = options.every((option) => option.value === expected(options[0]).value);
    if (!allOptionsMatch)
      dispatch(
        addFeatureGridConflictedField({ [propertyDataFieldName]: { fieldName, propertyId } }),
      );
  }, [fieldHasConflict, conflictIsResolved, options]);

  const sharedButtonStyle: Partial<ButtonProps> = {
    width: '100%',
    height: '32px',
    borderRadius: '0',
    color: theme.colors.textPrimary,
    padding: '4px 8px',
    fontWeight: 'normal',
    ...editableFieldOutlineSx,
    _active: {},
    _focus: {},
    _focusWithin: {},
    _hover: { backgroundColor: theme.colors.surface2 },
  };

  return (
    <Controller
      name={propertyDataFieldName}
      control={control}
      render={({ field: { onChange, ref }, fieldState: { error } }) => {
        const displayValue = toDisplayValue ? toDisplayValue(value, propertyId) : value;

        let editValue = toEditValue ? toEditValue(value, propertyId) : displayValue;
        const currentEditValue = toEditValue
          ? toEditValue(currentParsedValue, propertyId)
          : currentParsedValue;

        if (currentParsedValue !== undefined && currentEditValue !== editValue) {
          editValue = currentEditValue as string;
        }

        let selectedSource = selectedSourceValue ?? (isDefined(value) ? 'custom' : 'none');

        if (currentSource !== undefined && currentSource !== selectedSource) {
          selectedSource = currentSource;
        }

        const showIcon = isDefined(error) || selectedSource !== 'none';
        const icon = error
          ? 'error-warning'
          : getDataSourceDisplayInfo(selectedSource ?? 'none', undefined).icon;
        const iconColor = error ? 'alertError' : undefined;
        const iconTooltipLabel = error ? error.message : getSelectSourceTooltip(selectedSource);

        return (
          <Popover gutter={-32} initialFocusRef={customInput} variant="inline" isLazy matchWidth>
            {({ onClose }) => {
              const handleOnClose = () => {
                if (document.activeElement instanceof HTMLElement) document.activeElement?.blur();

                updatePropertyData();
                onClose();
              };

              let title: string | undefined;
              let backgroundColor = theme.colors.surface1;
              if (fieldHasConflict) {
                title = 'There is a data conflict. Please resolve.';
                backgroundColor = theme.colors.alertWarningLight;
              } else if (isUndefined(displayValue)) {
                title = 'This field is empty. Please input a value.';
                backgroundColor = theme.colors.selected4;
              }

              return (
                <>
                  <PopoverTrigger>
                    <Button
                      {...sharedButtonStyle}
                      title={title}
                      backgroundColor={backgroundColor}
                      gridColumn={`span ${colSpan}`}
                      onMouseEnter={() => setShowConflictButton(true)}
                      onMouseLeave={() => setShowConflictButton(false)}
                    >
                      <Flex alignItems="center" justifyContent="space-between" width="100%">
                        <Block
                          __dangerDoNotUseSx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayValue}
                        </Block>
                        <Flex alignItems="center" gap="0.25rem">
                          {fieldHasConflict && showConflictButton && (
                            <Block
                              title="This field has a conflict with another data source."
                              width="20px"
                              height="20px"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                dispatch(
                                  resolveFeatureGridFieldConflicts({
                                    [propertyDataFieldName]: expected(
                                      conflictedFields[propertyDataFieldName],
                                    ),
                                  }),
                                );
                              }}
                              __dangerDoNotUseSx={{
                                backgroundColor: theme.colors.alertSuccess,
                                border: `1px solid ${theme.colors.textSuccess}`,
                              }}
                            >
                              <Icon icon="check" color="textOffset" size="md" />
                            </Block>
                          )}
                          {showIcon ? (
                            <Tooltip label={iconTooltipLabel}>
                              <Icon icon={icon} color={iconColor} />
                            </Tooltip>
                          ) : (
                            <div style={{ width: '16px', height: '16px' }} aria-hidden />
                          )}
                        </Flex>
                      </Flex>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      height="32px"
                      gap="4px"
                      padding="4px 8px"
                      borderWidth="0px"
                      borderStyle="solid"
                      borderColor="strokeDefault"
                      borderBottomWidth="1px"
                      __dangerDoNotUseSx={{
                        ...(inputFocused && {
                          outline: '1.5px solid',
                          outlineOffset: '-1.5px',
                          outlineColor: theme.colors.selected2,
                        }),
                      }}
                    >
                      <Input
                        type="text"
                        ref={(instance) => {
                          ref(instance);
                          customInput.current = instance;
                        }}
                        value={editValue}
                        __dangerDoNotUseSx={{
                          height: '100%',
                          padding: '0',
                          border: 'none',
                          outline: 'none',
                          borderRadius: '0',
                          fontSize: '14px',
                          _focusVisible: {},
                        }}
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => {
                          handleSetFieldValue(onChange, editValue);
                          setInputFocused(false);
                          handleOnClose();
                        }}
                        onChange={(v) => handleSetFieldValue(onChange, v)}
                        onClear={() => {
                          handleSetFieldValue(onChange, undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setInputFocused(false);
                            onClose();
                          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                            handleSetFieldValue(onChange, editValue);
                            setInputFocused(false);
                            onClose();
                          }
                        }}
                      />
                      <Tooltip label={iconTooltipLabel}>
                        <Icon icon={icon} color={iconColor} />
                      </Tooltip>
                    </Flex>
                    <Flex flexDirection="column" gap="1px" maxHeight="25vh" overflowY="auto">
                      {options.map((option) => (
                        <Button
                          key={option.source}
                          title={option.value}
                          backgroundColor={theme.colors.surface1}
                          {...sharedButtonStyle}
                          onClick={() => {
                            const { value: newValue, source: newSource } = option;

                            handleSetFieldValue(onChange, newValue, newSource);
                            handleOnClose();
                          }}
                        >
                          <Flex alignItems="center" justifyContent="space-between" width="100%">
                            <Block
                              __dangerDoNotUseSx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Typography variant="body1">{option.value}</Typography>
                            </Block>
                            <Tooltip label={getSelectSourceTooltip(option.source)}>
                              <Icon
                                icon={getDataSourceDisplayInfo(option.source, undefined).icon}
                                color="buttonDefault"
                              />
                            </Tooltip>
                          </Flex>
                        </Button>
                      ))}
                    </Flex>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};
